// hooks/useItemOperations.js - ACTUALIZADO con nueva lógica de fechas
import { useCallback } from 'react';
import { groceriesApi } from '../api/groceriesApi';
import { getTodayDateString } from '../utils/itemUtils';
import i18n from '../i18n';

export const useItemOperations = (showToast) => {
  const normalizeLater = (later) => (parseInt(later) === 1 ? 1 : 0);
  const getListId = (item) => (item.list_id !== undefined && item.list_id !== null ? item.list_id : null);

  const editItem = useCallback(async (itemData) => {
    try {
      const result = await groceriesApi.editItem(itemData);
      if (!result.success) throw new Error(result.message || 'Error editing item');
      return result.data;
    } catch (err) {
      if (showToast) showToast(i18n.t('toasts.errorGeneric', { msg: err.message }), 'error');
      throw err;
    }
  }, [showToast]);

  const insertItem = useCallback(async (itemData) => {
    try {
      const result = await groceriesApi.insertItem(itemData);
      if (!result.success) throw new Error(result.message || 'Error adding item');
      return result.data;
    } catch (err) {
      if (showToast) showToast(i18n.t('toasts.errorGeneric', { msg: err.message }), 'error');
      throw err;
    }
  }, [showToast]);

  const deleteItem = useCallback(async (id) => {
    try {
      const result = await groceriesApi.deleteItem(id);
      if (!result.success) throw new Error(result.message || 'Error deleting item');
      return true;
    } catch (err) {
      if (showToast) showToast(i18n.t('toasts.errorGeneric', { msg: err.message }), 'error');
      throw err;
    }
  }, [showToast]);

  const updateItemField = useCallback(async (itemId, field, value, currentItem) => {
    try {
      let updateData = {
        id: itemId.toString(),
        item: currentItem.item,
        category: currentItem.category || 'others',
        status: currentItem.status || "0",
        price: currentItem.price || "0",
        quantity: currentItem.quantity || 0,
        note: currentItem.note || "",
        date: currentItem.date || "0000-00-00",
        list_id: getListId(currentItem),
        later: normalizeLater(currentItem.later)
      };

      if (field === 'rename') {
        updateData.item = value;
      } else if (field === 'price') {
        updateData.price = value.toString();
      } else if (field === 'category') {
        updateData.category = value;
      } else if (field === 'note') {
        updateData.note = value;
      } else if (field === 'list') {
        updateData.list_id = value;
      } else if (field === 'later') {
        updateData.later = normalizeLater(value);
      }

      const result = await groceriesApi.editItem(updateData);
      
      if (!result.success) {
        throw new Error(result.message || `Error updating ${field}`);
      }

      return result.data;

    } catch (err) {
      if (showToast) {
        showToast(i18n.t('toasts.errorUpdatingField', { field, msg: err.message }), 'error');
      }
      throw err;
    }
  }, [showToast]);

  const updateName = useCallback(async (itemId, newName, currentItem) => {
    return await updateItemField(itemId, 'rename', newName, currentItem);
  }, [updateItemField]);

  const updatePrice = useCallback(async (itemId, newPrice, currentItem) => {
    const priceValue = parseFloat(newPrice) || 0;
    return await updateItemField(itemId, 'price', priceValue, currentItem);
  }, [updateItemField]);

  const updateCategory = useCallback(async (itemId, newCategory, currentItem) => {
    return await updateItemField(itemId, 'category', newCategory, currentItem);
  }, [updateItemField]);

  const updateNote = useCallback(async (itemId, newNote, currentItem) => {
    return await updateItemField(itemId, 'note', newNote, currentItem);
  }, [updateItemField]);

  const updateList = useCallback(async (itemId, newListId, currentItem) => {
    return await updateItemField(itemId, 'list', newListId, currentItem);
  }, [updateItemField]);

  const updateLater = useCallback(async (item, laterValue) => {
    const updateData = {
      id: item.id.toString(),
      item: item.item,
      category: item.category || 'others',
      status: item.status || "0",
      price: parseFloat(item.price) || 0,
      quantity: item.quantity || 0,
      note: item.note || '',
      date: item.date || '0000-00-00',
      list_id: getListId(item),
      later: normalizeLater(laterValue)
    };

    return await editItem(updateData);
  }, [editItem]);

  // MODIFICADO: Al chequear/des-chequear, solo se toca la fecha cuando se chequea (status 1)
  const checkToggleItem = useCallback(async (item) => {
    const currentStatus = parseInt(item.status);
    const newStatus = currentStatus === 1 ? 0 : 1;
    
    const updateData = {
      id: item.id.toString(),
      item: item.item,
      category: item.category || 'others',
      status: newStatus,
      price: parseFloat(item.price) || 0,
      quantity: item.quantity || 0,
      note: item.note || '',
      // Solo actualizar fecha si se está chequeando (newStatus === 1)
      // Si se des-chequea, mantener la fecha que tenía
      date: newStatus === 1 ? getTodayDateString() : (item.date || '0000-00-00'),
      list_id: getListId(item),
      later: normalizeLater(item.later)
    };
    
    return await editItem(updateData);
  }, [editItem]);

  const updateQuantity = useCallback(async (item, newQuantity) => {
    const updateData = {
      id: item.id.toString(),
      item: item.item,
      category: item.category || 'others',
      status: item.status || "0",
      price: parseFloat(item.price) || 0,
      quantity: newQuantity,
      note: item.note || '',
      date: item.date || '0000-00-00',
      list_id: getListId(item),
      later: normalizeLater(item.later)
    };
    
    return await editItem(updateData);
  }, [editItem]);

  const addToList = useCallback(async (item, list) => {
    const listId = typeof list === 'object' && list !== null ? list.id : list;

    const updateData = {
      id: item.id.toString(),
      item: item.item,
      category: item.category || 'others',
      status: "0",                    
      price: parseFloat(item.price) || 0,
      quantity: 1,
      note: item.note || '',
      date: item.date || '0000-00-00',  // Mantener la fecha original
      list_id: listId || null,
      later: 0
    };
    
    return await editItem(updateData);
  }, [editItem]);

  // Remover de lista - limpiar list_id/quantity, pero mantener fecha y status.
  // Así conserva el estado de "chequeado hoy" si aplica
  const removeFromList = useCallback(async (item) => {
    const updateData = {
      id: item.id.toString(),
      item: item.item,
      category: item.category || 'others',
      status: item.status || "0",  // Mantener status original (si estaba chequeado, sigue chequeado)
      price: parseFloat(item.price) || 0,
      quantity: 0,
      note: item.note || '',
      date: item.date || '0000-00-00',  // Mantener la fecha del último chequeo
      list_id: null,
      later: 0
    };
    
    return await editItem(updateData);
  }, [editItem]);

  return {
    editItem,
    insertItem,
    deleteItem,
    checkToggleItem,
    updateQuantity,
    addToList,
    removeFromList,
    updateList,
    updateLater,
    updateItemField,
    updateName,
    updatePrice,
    updateCategory,
    updateNote
  };
};